import { useEffect, useMemo, useState } from "react"

import { cloneDeep } from "lodash"
import { PlusIcon, Trash } from "lucide-react"
import { Input, Button } from "twyne-new.ui"

import { useResponsive, useUtilities } from "@/modules/cms/views/post/components/builder/context"

import { useCraftProp } from "../../utils"

import Popover, { EASE_OPTIONS } from './Popover'

export default function TransitionProperty() {
  const { device } = useResponsive()
  const { selectedUtilities } = useUtilities()
  const { value: transition } = useCraftProp('transition')
  useConvertOldValue()

  if (transition && !Array.isArray(transition)) {
    return null
  }

  return <>
    <Transition key={device + selectedUtilities} />
  </>
}

const useConvertOldValue = () => {
  const { value: transition, setValue: setTransition } = useCraftProp('transition')

  useEffect(() => {
    if (transition && !Array.isArray(transition)) {
      setTransition([transition])
    }
  }, [transition, setTransition])
}

const Transition = () => {
  const { value: craftTransitions, setValue: setCraftTransitions } = useCraftProp('transition')
  const [transitions, setTransitions] = useState()

  useEffect(() => {
    if (transitions === undefined) {
      setTransitions(craftTransitions || [DEFAULT_VALUES])
    }
  }, [transitions, craftTransitions, setTransitions])

  const handleAdd = () => {
    const newTransition = [...craftTransitions || [], {}]
    setCraftTransitions(newTransition)
    setTransitions(newTransition)
  }

  const handleDelete = index => {
    const newTransition = craftTransitions.filter((_, i) => i !== index)
    setCraftTransitions(newTransition)
    setTransitions(newTransition)
  }

  const handleApply = () => {
    setCraftTransitions(transitions)
  }

  const handleChange = (index, newValue) => {
    const newTransitions = cloneDeep(transitions)
    newTransitions[index] = { ...newTransitions[index], ...newValue }
    setTransitions(newValue ? newTransitions : craftTransitions)
  }

  if (!transitions) {
    return null
  }

  return <>
    <div className='flex flex-col gap-2 mb-2'>
      {transitions?.map((_, index) => <PreviewTransition
        key={index}
        index={index}
        onApply={handleApply}
        onChange={value => handleChange(index, value)}
        onDelete={() => handleDelete(index)}
        transition={transitions[index]}
        transitions={transitions}
      />)}
    </div>
    <AddButton onAdd={handleAdd} />
  </>
}

const PreviewTransition = ({ index, onDelete, onApply, onChange, transition, transitions }) => <>
  <Popover index={index} onApply={onApply} onChange={onChange} transition={transition} transitions={transitions} >
    <div className='flex gap-2 justify-between'>
      <div className='w-full'>
        <InputPreview index={index} />
      </div>
      <Button variant='light' size='sm' onClick={onDelete}>
        <Trash size='14' />
      </Button>
    </div>
  </Popover>
</>

const InputPreview = ({ index }) => {
  const { value } = useCraftProp('transition')
  const transition = !value?.length ? [] : value
  const { duration, ease, property } = transition[index] || {}
  const transitions = useMemo(() => !property ? [] : Array.isArray(property) ? property : [property], [property])

  const inputValue = useMemo(() => {
    const transitionString = transitions.join('/')
    const durationString = duration ? `${duration}ms` : ''
    const easeString = ease ? `${EASE_OPTIONS.find(option => option.value === ease)?.label || ''}` : ''
    return [transitionString, durationString, easeString].filter(Boolean).join(', ')
  }, [duration, ease, transitions])

  return <div className='flex items-center gap-1 relative'>
    <Input value={inputValue || ''} readOnly variant='accent' />
  </div>
}

const AddButton = ({ onAdd }) => <>
  <div className='flex justify-end'>
    <Button variant='light' size='sm' onClick={onAdd}>
      <PlusIcon size='14' />
    </Button>
  </div>
</>

const DEFAULT_VALUES = {
  type: 'predefined',
  property: 'all',
  ease: 'ease-linear',
  duration: 1000,
}
